import React, { useRef, useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Copy, Maximize2, Minimize2 } from "lucide-react";

function CodeEditor({ value, onChange, language = "csharp" }) {
  const editorRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Generate line numbers
  const lines = value ? value.split("\n") : [""];

  // Sync scrolling between textarea and line numbers
  useEffect(() => {
    const editor = editorRef.current;
    const lineNumbers = lineNumbersRef.current;

    if (!editor || !lineNumbers) return;

    const handleScroll = () => {
      lineNumbers.scrollTop = editor.scrollTop;
    };

    editor.addEventListener("scroll", handleScroll);
    return () => {
      editor.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Update line numbers height to match textarea
  useEffect(() => {
    const editor = editorRef.current;
    const lineNumbers = lineNumbersRef.current;

    if (!editor || !lineNumbers) return;

    // Set line height to match text area
    const lineHeight =
      parseInt(window.getComputedStyle(editor).lineHeight, 10) || 20;
    lineNumbers.style.lineHeight = `${lineHeight}px`;
  }, []);

  // Handle tab key in textarea
  useEffect(() => {
    const handleTabKey = (e) => {
      if (e.key === "Tab") {
        e.preventDefault();

        const start = e.target.selectionStart;
        const end = e.target.selectionEnd;

        // If multiple lines are selected, indent them all
        if (start !== end && value.substring(start, end).includes("\n")) {
          const selectedText = value.substring(start, end);
          const lines = selectedText.split("\n");
          const indentedText = lines.map((line) => "    " + line).join("\n");

          const newValue =
            value.substring(0, start) + indentedText + value.substring(end);
          onChange(newValue);

          // Set new selection range
          setTimeout(() => {
            e.target.selectionStart = start;
            e.target.selectionEnd = start + indentedText.length;
          }, 0);
        } else {
          // Just insert a tab at cursor position
          const newValue =
            value.substring(0, start) + "    " + value.substring(end);
          onChange(newValue);

          // Put cursor at right position after tab
          setTimeout(() => {
            e.target.selectionStart = e.target.selectionEnd = start + 4;
          }, 0);
        }
      }
    };

    const editor = editorRef.current;
    if (editor) {
      editor.addEventListener("keydown", handleTabKey);
    }

    return () => {
      if (editor) {
        editor.removeEventListener("keydown", handleTabKey);
      }
    };
  }, [value, onChange]);

  // Handle copy action
  const handleCopy = () => {
    if (value) {
      navigator.clipboard
        .writeText(value)
        .then(() => {
          console.log("Code copied to clipboard");
        })
        .catch((err) => {
          console.error("Could not copy text: ", err);
        });
    }
  };

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div
      className={`relative h-full border rounded-md overflow-hidden ${
        isFocused ? "ring-2 ring-blue-500" : ""
      } ${isFullscreen ? "fixed inset-0 z-50 bg-white" : ""}`}
    >
      <div className="absolute top-2 right-2 z-10 flex space-x-2">
        <Button
          size="sm"
          variant="outline"
          onClick={toggleFullscreen}
          title={isFullscreen ? "Exit fullscreen" : "Fullscreen mode"}
        >
          {isFullscreen ? (
            <Minimize2 className="h-4 w-4" />
          ) : (
            <Maximize2 className="h-4 w-4" />
          )}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={handleCopy}
          title="Copy to clipboard"
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>

      <div className={`flex h-full ${isFullscreen ? "p-4" : ""}`}>
        {/* Line numbers column */}
        <div
          ref={lineNumbersRef}
          className="bg-gray-100 text-gray-500 text-right pr-2 pt-4 select-none overflow-hidden"
          style={{ width: "50px", minWidth: "50px" }}
        >
          {lines.map((_, idx) => (
            <div key={idx} className="text-xs">
              {idx + 1}
            </div>
          ))}
        </div>

        {/* Editor column */}
        <textarea
          ref={editorRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className="w-full h-full py-4 pr-2 pl-0 font-mono text-sm resize-none focus:outline-none"
          placeholder={`Enter your ${language} code here...`}
          spellCheck="false"
          style={{ paddingLeft: 0 }}
        />
      </div>

      {/* Language indicator */}
      <div className="absolute bottom-2 right-2 text-xs text-gray-400 bg-white px-2 py-1 rounded">
        {language.toUpperCase()}
      </div>
    </div>
  );
}

export default CodeEditor;
